import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n)||0);

const CATEGORIAS = [
  'Arriendo', 'Servicios públicos', 'Internet / Telefonía', 'Nómina',
  'Seguridad social', 'Mantenimiento', 'Marketing / Publicidad',
  'Transporte / Fletes', 'Empaques / Insumos', 'Limpieza / Aseo',
  'Compras directas PDV', 'Honorarios', 'Impuestos / Tasas', 'Otros',
];

const st = {
  label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' },
  input: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white', width: '100%', boxSizing: 'border-box' },
  btnPrimary: { padding: '9px 22px', background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnSec: { padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

// ── Tab Gastos ────────────────────────────────────────────
function TabGastos() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [gastos, setGastos]         = useState([]);
  const [centros, setCentros]       = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [desde, setDesde]           = useState(inicioMes);
  const [hasta, setHasta]           = useState(hoy);
  const [filtroCentro, setFiltroCentro] = useState('');
  const [modal, setModal]           = useState(false);
  const [editando, setEditando]     = useState(null);
  const [guardando, setGuardando]   = useState(false);
  const [form, setForm] = useState({
    centro_costo_id: '', fecha: hoy, categoria: '', descripcion: '',
    proveedor_id: '', monto: '', tiene_factura: true, numero_factura: '', notas: '', metodo_pago: ''
  });

  useEffect(() => {
    API.get('/contable/centros-costo').then(r => setCentros(r.data)).catch(() => {});
    API.get('/proveedores').then(r => setProveedores(r.data)).catch(() => {});
    cargar();
  }, []);

  const cargar = () => {
    setCargando(true);
    API.get('/contable/gastos', { params: { desde, hasta, centro_costo_id: filtroCentro || undefined } })
      .then(r => setGastos(r.data))
      .catch(() => toast.error('Error cargando gastos'))
      .finally(() => setCargando(false));
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ centro_costo_id: '', fecha: hoy, categoria: '', descripcion: '', proveedor_id: '', monto: '', tiene_factura: true, numero_factura: '', notas: '', metodo_pago: '' });
    setModal(true);
  };

  const abrirEditar = (g) => {
    setEditando(g.id);
    setForm({
      centro_costo_id: String(g.centro_costo_id), fecha: g.fecha?.slice(0,10),
      categoria: g.categoria, descripcion: g.descripcion,
      proveedor_id: g.proveedor_id ? String(g.proveedor_id) : '',
      monto: g.monto, tiene_factura: g.tiene_factura,
      numero_factura: g.numero_factura||'', notas: g.notas||'', metodo_pago: g.metodo_pago||''
    });
    setModal(true);
  };

  const guardar = async () => {
    if (!form.centro_costo_id || !form.fecha || !form.categoria || !form.descripcion || !form.monto)
      return toast.error('Completa todos los campos requeridos');
    setGuardando(true);
    try {
      if (editando) await API.put(`/contable/gastos/${editando}`, form);
      else await API.post('/contable/gastos', form);
      toast.success(editando ? 'Gasto actualizado' : '✅ Gasto registrado');
      setModal(false); cargar();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try { await API.delete(`/contable/gastos/${id}`); toast.success('Eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const total = gastos.reduce((s, g) => s + parseFloat(g.monto||0), 0);
  const porCategoria = gastos.reduce((m, g) => { m[g.categoria] = (m[g.categoria]||0) + parseFloat(g.monto||0); return m; }, {});

  return (
    <div>
      {/* Controles */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><span style={st.label}>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div><span style={st.label}>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div><span style={st.label}>Centro de costo</span>
          <select value={filtroCentro} onChange={e => setFiltroCentro(e.target.value)} style={{ ...st.input, width: 200 }}>
            <option value="">Todos</option>
            {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <button onClick={cargar} style={st.btnSec}>🔍 Consultar</button>
        <button onClick={abrirNuevo} style={{ ...st.btnPrimary, marginLeft: 'auto' }}>+ Registrar gasto</button>
      </div>

      {/* Resumen por categoría */}
      {gastos.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {Object.entries(porCategoria).sort((a,b) => b[1]-a[1]).map(([cat, val]) => (
            <div key={cat} style={{ background: 'white', borderRadius: 10, padding: '8px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', fontSize: 12 }}>
              <div style={{ color: '#6B7280', marginBottom: 2 }}>{cat}</div>
              <div style={{ fontWeight: 800, color: '#EF4444', fontFamily: 'monospace' }}>{COP(val)}</div>
            </div>
          ))}
          <div style={{ background: '#1a1a2e', borderRadius: 10, padding: '8px 14px', fontSize: 12 }}>
            <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 2 }}>Total</div>
            <div style={{ fontWeight: 800, color: 'white', fontFamily: 'monospace' }}>{COP(total)}</div>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Fecha', 'Centro', 'Categoría', 'Descripción', 'Proveedor', 'Pago', 'Factura', 'Monto', ''].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Monto' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</td></tr>
            ) : gastos.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Sin gastos en este período</td></tr>
            ) : gastos.map((g, i) => (
              <tr key={g.id} style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                <td style={{ padding: '9px 12px', color: '#6B7280', whiteSpace: 'nowrap' }}>{g.fecha?.slice(0,10)}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: g.centro_tipo === 'pdv' ? '#EFF6FF' : g.centro_tipo === 'produccion' ? '#F0FDF4' : '#FEF3C7', color: g.centro_tipo === 'pdv' ? '#1D4ED8' : g.centro_tipo === 'produccion' ? '#065F46' : '#92400E' }}>
                    {g.centro_nombre}
                  </span>
                </td>
                <td style={{ padding: '9px 12px', color: '#374151' }}>{g.categoria}</td>
                <td style={{ padding: '9px 12px', color: '#374151' }}>{g.descripcion}</td>
                <td style={{ padding: '9px 12px', color: '#6B7280' }}>{g.proveedor_nombre || '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 12, color: '#6B7280' }}>
                  {g.metodo_pago ? (g.metodo_pago === 'efectivo' ? '💵 Efectivo' : g.metodo_pago.startsWith('bc_') ? '🏦 Bancolombia' : '🏦 Davivienda') : '—'}
                </td>
                <td style={{ padding: '9px 12px' }}>
                  {g.tiene_factura
                    ? <span style={{ color: '#059669', fontSize: 11 }}>✅ {g.numero_factura || ''}</span>
                    : <span style={{ color: '#F59E0B', fontSize: 11 }}>Sin factura</span>}
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#EF4444' }}>{COP(g.monto)}</td>
                <td style={{ padding: '9px 12px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => abrirEditar(g)} style={{ padding: '3px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11 }}>✏️</button>
                    <button onClick={() => eliminar(g.id)} style={{ padding: '3px 8px', border: '1px solid #FECACA', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11, color: '#EF4444' }}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#1a1a2e', padding: '14px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'white', fontWeight: 800 }}>{editando ? '✏️ Editar gasto' : '+ Registrar gasto'}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={st.label}>Centro de costo *</span>
                  <select value={form.centro_costo_id} onChange={e => setForm(f=>({...f,centro_costo_id:e.target.value}))} style={st.input}>
                    <option value="">Seleccionar...</option>
                    {['pdv','produccion','administracion'].map(tipo => (
                      <optgroup key={tipo} label={tipo === 'pdv' ? '🏪 Puntos de venta' : tipo === 'produccion' ? '🏭 Producción' : '🏢 Administración'}>
                        {centros.filter(c => c.tipo === tipo).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div><span style={st.label}>Fecha *</span><input type="date" value={form.fecha} onChange={e => setForm(f=>({...f,fecha:e.target.value}))} style={st.input} /></div>
                <div>
                  <span style={st.label}>Categoría *</span>
                  <select value={form.categoria} onChange={e => setForm(f=>({...f,categoria:e.target.value}))} style={st.input}>
                    <option value="">Seleccionar...</option>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><span style={st.label}>Monto *</span><input type="number" value={form.monto} onChange={e => setForm(f=>({...f,monto:e.target.value}))} placeholder="0" style={st.input} /></div>
              </div>
              <div><span style={st.label}>Descripción *</span><input value={form.descripcion} onChange={e => setForm(f=>({...f,descripcion:e.target.value}))} placeholder="Descripción del gasto" style={st.input} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={st.label}>Proveedor</span>
                  <select value={form.proveedor_id} onChange={e => setForm(f=>({...f,proveedor_id:e.target.value}))} style={st.input}>
                    <option value="">Sin proveedor</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <span style={st.label}>¿Tiene factura?</span>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    {[true, false].map(v => (
                      <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input type="radio" checked={form.tiene_factura === v} onChange={() => setForm(f=>({...f,tiene_factura:v}))} />
                        {v ? '✅ Sí' : '❌ No'}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              {form.tiene_factura && (
                <div><span style={st.label}>Número de factura</span><input value={form.numero_factura} onChange={e => setForm(f=>({...f,numero_factura:e.target.value}))} placeholder="Opcional" style={st.input} /></div>
              )}
              <div><span style={st.label}>Notas</span><input value={form.notas} onChange={e => setForm(f=>({...f,notas:e.target.value}))} placeholder="Opcional" style={st.input} /></div>
                <div><span style={st.label}>Método de pago</span>
                  <select value={form.metodo_pago} onChange={e => setForm(f=>({...f,metodo_pago:e.target.value}))} style={st.input}>
                    <option value="">Sin especificar</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="bc_gelatus">Bancolombia · Gelatus SAS ···9449</option>
                    <option value="dv_gelatus">Davivienda · Gelatus SAS ···1783</option>
                    <option value="bc_jhon">Bancolombia · Jhon Ruiz ···8644</option>
                    <option value="dv_jhon">Davivienda · Jhon Ruiz ···2452</option>
                    <option value="bc_betty">Bancolombia · Betty De Vargas ···0891</option>
                    <option value="dv_betty">Davivienda · Betty De Vargas ···8204</option>
                  </select>
                </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={() => setModal(false)} style={st.btnSec}>Cancelar</button>
                <button onClick={guardar} disabled={guardando} style={st.btnPrimary}>{guardando ? '⏳' : '💾 Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Estado de Resultados ──────────────────────────────
function TabEstadoResultados() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoy);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = () => {
    setCargando(true); setDatos(null);
    API.get('/contable/estado-resultados', { params: { desde, hasta } })
      .then(r => setDatos(r.data))
      .catch(() => toast.error('Error cargando estado de resultados'))
      .finally(() => setCargando(false));
  };

  const colorMargen = (m) => m > 0 ? '#059669' : m < 0 ? '#EF4444' : '#6B7280';
  const tipoBadge = (tipo) => {
    const cfg = { pdv: ['🏪', '#EFF6FF', '#1D4ED8'], produccion: ['🏭', '#F0FDF4', '#065F46'], administracion: ['🏢', '#FEF3C7', '#92400E'] };
    return cfg[tipo] || ['', '#F9FAFB', '#374151'];
  };

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><span style={st.label}>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div><span style={st.label}>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <button onClick={cargar} disabled={cargando} style={st.btnPrimary}>{cargando ? '⏳' : '🔍 Calcular'}</button>
      </div>

      {datos && (
        <>
          {/* Tarjetas resumen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Ingresos totales', val: datos.totales.ingresos, color: '#059669' },
              { label: 'Costo de envíos', val: datos.totales.costo_envios, color: '#F59E0B' },
              { label: 'Gastos totales', val: datos.totales.total_gastos, color: '#EF4444' },
              { label: 'Margen neto', val: datos.totales.margen, color: colorMargen(datos.totales.margen) },
            ].map(card => (
              <div key={card.label} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>{card.label}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18, color: card.color }}>{COP(card.val)}</div>
              </div>
            ))}
          </div>

          {/* Tabla por centro */}
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ background: '#1a1a2e', padding: '11px 18px' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>📒 Estado de Resultados por Centro de Costo</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginLeft: 10 }}>{desde} → {hasta}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Centro', 'Tipo', 'Ingresos', 'Costo envíos', 'Gastos', 'Margen', '%'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: ['Ingresos','Costo envíos','Gastos','Margen','%'].includes(h) ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.resultado.map((c, i) => {
                  const [ico, bg, textC] = tipoBadge(c.tipo);
                  return (
                    <tr key={c.centro_id} style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700 }}>{c.centro}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: bg, color: textC }}>{ico} {c.tipo}</span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 700 }}>
                        {c.ingresos > 0 ? COP(c.ingresos) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#F59E0B' }}>
                        {c.costo_envios > 0 ? COP(c.costo_envios) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#EF4444' }}>
                        {c.total_gastos > 0 ? COP(c.total_gastos) : '—'}
                        {Object.keys(c.gastos).length > 0 && (
                          <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                            {Object.entries(c.gastos).map(([k,v]) => `${k}: ${COP(v)}`).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: colorMargen(c.margen) }}>
                        {COP(c.margen)}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: colorMargen(c.margen) }}>
                        {c.margen_pct !== null ? `${c.margen_pct}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #E5E7EB', background: '#1a1a2e' }}>
                  <td colSpan={2} style={{ padding: '11px 14px', color: 'white', fontWeight: 800 }}>TOTAL EMPRESA</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#6EE7B7' }}>{COP(datos.totales.ingresos)}</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#FCD34D' }}>{COP(datos.totales.costo_envios)}</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#FCA5A5' }}>{COP(datos.totales.total_gastos)}</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: datos.totales.margen >= 0 ? '#6EE7B7' : '#FCA5A5', fontSize: 15 }}>{COP(datos.totales.margen)}</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 800, color: datos.totales.margen >= 0 ? '#6EE7B7' : '#FCA5A5' }}>
                    {datos.totales.ingresos > 0 ? `${((datos.totales.margen/datos.totales.ingresos)*100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab Flujo de Caja ─────────────────────────────────────
function TabFlujoCaja() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoy);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [expandido, setExpandido] = useState({});

  const cargar = () => {
    setCargando(true); setDatos(null);
    API.get('/contable/flujo-caja', { params: { desde, hasta } })
      .then(r => setDatos(r.data))
      .catch(() => toast.error('Error cargando flujo de caja'))
      .finally(() => setCargando(false));
  };

  const toggle = (fecha) => setExpandido(e => ({ ...e, [fecha]: !e[fecha] }));

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><span style={st.label}>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div><span style={st.label}>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <button onClick={cargar} disabled={cargando} style={st.btnPrimary}>{cargando ? '⏳' : '🔍 Consultar'}</button>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 'auto', alignSelf: 'center' }}>
          Entradas = cierres de caja cerrados · Salidas = gastos registrados
        </div>
      </div>

      {datos && (
        <>
          {/* Tarjetas resumen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Total entradas', val: datos.totales.total_entradas, color: '#059669', icon: '📈' },
              { label: 'Total salidas', val: datos.totales.total_salidas, color: '#EF4444', icon: '📉' },
              { label: 'Saldo neto', val: datos.totales.saldo_neto, color: datos.totales.saldo_neto >= 0 ? '#059669' : '#EF4444', icon: '💰' },
            ].map(card => (
              <div key={card.label} style={{ background: 'white', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>{card.icon} {card.label}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20, color: card.color }}>{COP(card.val)}</div>
              </div>
            ))}
          </div>

          {/* Tabla por día */}
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ background: '#1a1a2e', padding: '11px 18px' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>💰 Flujo de Caja Diario</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha', 'Entradas', 'Salidas', 'Saldo día', 'Saldo acumulado', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Fecha' || h === '' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.resultado.map((d, i) => (
                  <React.Fragment key={d.fecha}>
                    <tr style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA', cursor: 'pointer' }}
                        onClick={() => toggle(d.fecha)}>
                      <td style={{ padding: '10px 14px', fontWeight: 700 }}>
                        {format(new Date(d.fecha+'T12:00'), "EEEE d MMM", { locale: es })}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 700 }}>
                        {d.total_entradas > 0 ? COP(d.total_entradas) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#EF4444', fontWeight: 700 }}>
                        {d.total_salidas > 0 ? COP(d.total_salidas) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: d.saldo >= 0 ? '#059669' : '#EF4444' }}>
                        {COP(d.saldo)}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: d.saldo_acumulado >= 0 ? '#1D4ED8' : '#EF4444', fontSize: 14 }}>
                        {COP(d.saldo_acumulado)}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#9CA3AF', fontSize: 12 }}>
                        {expandido[d.fecha] ? '▲' : '▼'}
                      </td>
                    </tr>
                    {expandido[d.fecha] && (
                      <tr style={{ background: '#F8FAFF' }}>
                        <td colSpan={6} style={{ padding: '0 20px 12px 20px' }}>
                          {d.entradas.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: 6 }}>📈 Entradas</div>
                              {d.entradas.map((e, j) => (
                                <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #E5E7EB' }}>
                                  <span style={{ color: '#374151' }}>{e.bodega}</span>
                                  <span style={{ color: '#6B7280', fontSize: 11 }}>Ef: {COP(e.efectivo)} · Banco: {COP(e.banco)} · Rappi: {COP(e.rappi)}</span>
                                  <span style={{ fontWeight: 700, color: '#059669', fontFamily: 'monospace' }}>{COP(e.total)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {d.salidas.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', textTransform: 'uppercase', marginBottom: 6 }}>📉 Salidas</div>
                              {d.salidas.map((s, j) => (
                                <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #E5E7EB' }}>
                                  <span style={{ color: '#374151' }}>{s.centro} · {s.categoria}</span>
                                  <span style={{ color: '#6B7280' }}>{s.descripcion}</span>
                                  <span style={{ fontWeight: 700, color: '#EF4444', fontFamily: 'monospace' }}>{COP(s.monto)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────
export default function ModuloContable() {
  const { puedeVer } = useAuth();
  if (!puedeVer('gerencia')) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
      No tienes permiso para ver este módulo.
    </div>
  );

  const [tab, setTab] = useState('gastos');
  const TABS = [
    { id: 'gastos',         label: '📋 Gastos' },
    { id: 'consignaciones', label: '🏦 Consignaciones' },
    { id: 'resultados',     label: '📒 Estado de Resultados' },
    { id: 'flujo',          label: '💰 Flujo de Caja' },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📒 Módulo Contable</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Gastos, Estado de Resultados y Flujo de Caja — solo gerencia</p>
      </div>

      <div style={{ display: 'flex', gap: 3, marginBottom: 20, background: '#F3F4F6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 20px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? 'white' : 'transparent',
            color: tab === t.id ? '#111827' : '#6B7280',
            boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'gastos'         && <TabGastos />}
      {tab === 'consignaciones' && <TabConsignaciones />}
      {tab === 'resultados'     && <TabEstadoResultados />}
      {tab === 'flujo'          && <TabFlujoCaja />}
    </div>
  );
}

// ── Tab Consignaciones ────────────────────────────────────
function TabConsignaciones() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoy);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [filtroMetodo, setFiltroMetodo] = useState('');

  const cargar = () => {
    setCargando(true); setDatos(null);
    const params = { desde, hasta };
    if (filtroMetodo) params.metodo_pago = filtroMetodo;
    API.get('/contable/consignaciones', { params })
      .then(r => setDatos(r.data))
      .catch(() => toast.error('Error cargando consignaciones'))
      .finally(() => setCargando(false));
  };

  const METODO_LABEL = {
    bc_gelatus: 'Bancolombia · Gelatus SAS ···9449',
    dv_gelatus: 'Davivienda · Gelatus SAS ···1783',
    bc_jhon:    'Bancolombia · Jhon Ruiz ···8644',
    dv_jhon:    'Davivienda · Jhon Ruiz ···2452',
    bc_betty:   'Bancolombia · Betty De Vargas ···0891',
    dv_betty:   'Davivienda · Betty De Vargas ···8204',
  };

  const colorMetodo = (m) => m?.startsWith('bc_') ? '#1D4ED8' : '#059669';
  const bgMetodo = (m) => m?.startsWith('bc_') ? '#EFF6FF' : '#F0FDF4';

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><span style={st.label}>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div><span style={st.label}>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={{ ...st.input, width: 150 }} /></div>
        <div>
          <span style={st.label}>Cuenta</span>
          <select value={filtroMetodo} onChange={e => setFiltroMetodo(e.target.value)} style={{ ...st.input, width: 260 }}>
            <option value="">Todas las cuentas</option>
            {Object.entries(METODO_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button onClick={cargar} disabled={cargando} style={st.btnPrimary}>{cargando ? '⏳' : '🔍 Consultar'}</button>
      </div>

      {datos && (
        <>
          {/* Totales por cuenta */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {datos.totales.map(t => (
              <div key={t.metodo_pago||'sin'} style={{ background: 'white', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', minWidth: 200 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: colorMetodo(t.metodo_pago), marginBottom: 4 }}>
                  {METODO_LABEL[t.metodo_pago] || t.cuenta_destino || 'Sin clasificar'}
                </div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18, color: '#059669' }}>{COP(t.total)}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{t.cnt} consignaciones</div>
              </div>
            ))}
            <div style={{ background: '#1a1a2e', borderRadius: 12, padding: '12px 16px', minWidth: 150 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>Total período</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18, color: '#6EE7B7' }}>
                {COP(datos.totales.reduce((s,t) => s + parseFloat(t.total||0), 0))}
              </div>
            </div>
          </div>

          {/* Tabla consignaciones */}
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ background: '#1a1a2e', padding: '11px 18px' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>🏦 Consignaciones registradas</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha', 'PDV', 'Cuenta bancaria', 'NIT receptor', 'Monto', 'Soporte'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h==='Monto'?'right':'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.consignaciones.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Sin consignaciones en este período</td></tr>
                ) : datos.consignaciones.map((c, i) => (
                  <tr key={c.id} style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding: '10px 14px', color: '#6B7280', whiteSpace: 'nowrap' }}>{c.fecha?.slice(0,10)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{c.bodega}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: bgMetodo(c.metodo_pago), color: colorMetodo(c.metodo_pago) }}>
                        {METODO_LABEL[c.metodo_pago] || c.cuenta_destino || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6B7280', fontFamily: 'monospace', fontSize: 12 }}>
                      {c.nit_receptor || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#059669' }}>
                      {COP(c.total_efectivo)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {c.foto_consignacion
                        ? <a href={`/uploads/consignaciones/${c.foto_consignacion}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563EB' }}>📷 Ver</a>
                        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
